import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { Calendar, CheckCircle, Loader2, Plus, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import {
  PeriodModal,
  formatPickerDate,
  getCalendarBounds,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { categorizeCondicaoRow } from '../../process-config/components/condicoesIntervShared';
import CustomersNoticeFmtBar from '../../users-customers/components/CustomersNoticeFmtBar';
import CustomersNoticeOryaCompose from '../../users-customers/components/CustomersNoticeOryaCompose';
import { useComposerVoiceRecorder } from './ComposerVoiceRecorder';
import { fetchChatSession, patchConversationOs } from '../services/boardChatService';
import '../../users-customers/components/CustomersNoticeFmtBar.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import './BoardChatPreAtendimentoCloseModal.css';

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function partsFromCreatedAt(createdAt) {
  let d;
  if (createdAt == null || createdAt === '') {
    d = new Date();
  } else if (typeof createdAt === 'number') {
    d = new Date(createdAt < 1e12 ? createdAt * 1000 : createdAt);
  } else {
    const raw = String(createdAt).trim();
    const asNum = Number(raw);
    if (raw !== '' && Number.isFinite(asNum) && !raw.includes('-') && !raw.includes('T') && !raw.includes('/')) {
      d = new Date(asNum < 1e12 ? asNum * 1000 : asNum);
    } else {
      d = new Date(raw);
    }
  }
  if (Number.isNaN(d.getTime())) {
    return { date: todayIso(), time: nowTime() };
  }
  return {
    date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
}

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
}

function isFlagOn(v) {
  return v === 1 || v === true || String(v) === '1';
}

function condicaoLabel(c) {
  const nome = String(c?.TB01055_NOME || '').trim();
  const cod = String(c?.TB01055_CODIGO || '').trim();
  if (!nome) return cod;
  if (cod && !nome.toLowerCase().startsWith(cod.toLowerCase())) {
    return `${cod} - ${nome}`;
  }
  return nome;
}

function caretCoords(textarea) {
  if (!textarea) return null;
  const r = textarea.getBoundingClientRect();
  return { top: r.top + 8, left: r.left + 12 };
}

function selectionBarPosition(textarea) {
  if (!textarea) return null;
  const r = textarea.getBoundingClientRect();
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  if (end <= start) return null;
  const len = Math.max(1, String(textarea.value || '').length);
  const mid = (start + end) / 2 / len;
  const left = Math.min(Math.max(8, r.left + mid * r.width - 90), window.innerWidth - 200);
  const top = Math.max(8, r.top - 40);
  return { top, left };
}

function toMs(dateYmd, timeHm) {
  const [y, m, d] = String(dateYmd || '')
    .split('-')
    .map((x) => parseInt(x, 10));
  const [hh, mm] = String(timeHm || '00:00')
    .split(':')
    .map((x) => parseInt(x, 10));
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0).getTime();
}

export function BoardChatPreAtendimentoCloseModal({
  open,
  osId,
  osData,
  createdAt,
  conversationId,
  agentCodtech,
  onClose,
  onClosed,
}) {
  const [form, setForm] = useState({
    dataInicio: todayIso(),
    horaInicio: nowTime(),
    dataTermino: todayIso(),
    horaTermino: nowTime(),
    condicaoFinal: '',
    codigoCondicao: '',
    contadores: { a4pb: '', a4cor: '', dig: '', a3pb: '', a3cor: '' },
    laudoTecnico: '',
  });
  const [condicoes, setCondicoes] = useState([]);
  const [condicaoQuery, setCondicaoQuery] = useState('');
  const [condicaoOpen, setCondicaoOpen] = useState(false);
  const [showCounters, setShowCounters] = useState(false);
  const [showA3, setShowA3] = useState(false);
  const [loadingCond, setLoadingCond] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [linkedCodtech, setLinkedCodtech] = useState(agentCodtech || '');
  const [datePicker, setDatePicker] = useState(null);
  const [oryaCompose, setOryaCompose] = useState(null);
  const [fmt, setFmt] = useState(null);
  const [voiceAnchor, setVoiceAnchor] = useState(null);
  const [voiceError, setVoiceError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusOptionsLoading, setStatusOptionsLoading] = useState(false);
  const [selectedStatusCode, setSelectedStatusCode] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState(null);
  const [statusSelectOpen, setStatusSelectOpen] = useState(false);
  const statusSelectRef = useRef(null);

  const condRef = useRef(null);
  const laudoRef = useRef(null);
  const laudoBodyRef = useRef(null);
  const selRef = useRef({ start: 0, end: 0, text: '' });
  const fmtOpenRef = useRef(false);
  fmtOpenRef.current = Boolean(fmt);
  const dateIniBtnRef = useRef(null);
  const dateFimBtnRef = useRef(null);

  const voiceRecorder = useComposerVoiceRecorder({});
  const voiceRecRef = useRef(voiceRecorder);
  voiceRecRef.current = voiceRecorder;
  const voiceActive = Boolean(voiceRecorder.recording);

  const canSave = Boolean(
    form.dataInicio &&
      form.horaInicio &&
      form.dataTermino &&
      form.horaTermino &&
      form.codigoCondicao &&
      form.laudoTecnico.trim() &&
      String(linkedCodtech || agentCodtech || '').trim() &&
      toMs(form.dataInicio, form.horaInicio) <= toMs(form.dataTermino, form.horaTermino)
  );

  useEffect(() => {
    if (!open) return undefined;
    const ini = partsFromCreatedAt(createdAt);
    const fim = { date: todayIso(), time: nowTime() };
    setForm({
      dataInicio: ini.date,
      horaInicio: ini.time,
      dataTermino: fim.date,
      horaTermino: fim.time,
      condicaoFinal: '',
      codigoCondicao: '',
      contadores: { a4pb: '', a4cor: '', dig: '', a3pb: '', a3cor: '' },
      laudoTecnico: '',
    });
    setCondicaoQuery('');
    setCondicaoOpen(false);
    setShowCounters(false);
    setShowA3(false);
    setError('');
    setDatePicker(null);
    setOryaCompose(null);
    setFmt(null);
    setVoiceAnchor(null);
    setVoiceError('');
    setShowStatusModal(false);
    setStatusOptions([]);
    setSelectedStatusCode('');
    setStatusFeedback(null);
    setStatusSelectOpen(false);
    setLinkedCodtech(agentCodtech || '');
    fetchChatSession()
      .then((res) => {
        const code = res?.data?.agentCodtech;
        if (code) setLinkedCodtech(String(code).trim());
      })
      .catch(() => {});
    setLoadingCond(true);
    fetch(`${getApiBaseUrl()}/api/config/condicoes-intervencao`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const rows = Array.isArray(d?.data) ? d.data : [];
        const allowed = rows.filter((row) => {
          if (categorizeCondicaoRow(row) !== 'nada') return false;
          if (isFlagOn(row.func_uninstall)) return false;
          if (isFlagOn(row.func_nao_liga)) return false;
          return true;
        });
        setCondicoes(allowed);
      })
      .catch(() => setCondicoes([]))
      .finally(() => setLoadingCond(false));
    return undefined;
  }, [open, osId, createdAt, agentCodtech]);

  useEffect(() => {
    if (!condicaoOpen) return undefined;
    const onDoc = (e) => {
      if (condRef.current?.contains(e.target)) return;
      setCondicaoOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [condicaoOpen]);

  useEffect(() => {
    if (!statusSelectOpen) return undefined;
    const onDoc = (e) => {
      if (statusSelectRef.current?.contains(e.target)) return;
      setStatusSelectOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [statusSelectOpen]);

  useEffect(() => {
    if (!showStatusModal) return undefined;
    let cancelled = false;
    setStatusOptionsLoading(true);
    fetch(`${getApiBaseUrl()}/api/oficina/opcoes-status-conclusao`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setStatusOptions(Array.isArray(d?.data) ? d.data : []);
      })
      .catch(() => {
        if (!cancelled) setStatusOptions([]);
      })
      .finally(() => {
        if (!cancelled) setStatusOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showStatusModal]);

  const finishCloseFlow = useCallback(() => {
    setShowStatusModal(false);
    onClosed?.();
  }, [onClosed]);

  const handleConfirmStatus = async () => {
    if (!osId || !selectedStatusCode || statusSaving) return;
    setStatusSaving(true);
    setStatusFeedback(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/oficina/alterar-status-conclusao`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ numeroOS: osId, codNovoStatus: selectedStatusCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setStatusFeedback('error');
        setTimeout(() => setStatusFeedback(null), 2800);
        return;
      }
      setStatusFeedback('success');
      setTimeout(() => finishCloseFlow(), 1200);
    } catch {
      setStatusFeedback('error');
      setTimeout(() => setStatusFeedback(null), 2800);
    } finally {
      setStatusSaving(false);
    }
  };

  const stopVoice = useCallback(async () => {
    const rec = voiceRecRef.current;
    if (rec?.recording) await rec.cancel();
    setVoiceAnchor(null);
    setVoiceError('');
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    return () => {
      stopVoice();
    };
  }, [open, stopVoice]);

  const condicoesFiltered = useMemo(() => {
    const q = String(condicaoQuery || '').trim().toLowerCase();
    if (q.length < 4) return [];
    return condicoes.filter((c) => {
      const label = condicaoLabel(c).toLowerCase();
      const cod = String(c.TB01055_CODIGO || '').toLowerCase();
      return label.includes(q) || cod.includes(q);
    });
  }, [condicoes, condicaoQuery]);

  const syncSelection = useCallback(() => {
    const el = laudoRef.current;
    if (!el || oryaCompose) {
      if (oryaCompose) setFmt(null);
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (end <= start) {
      // Com a barra Orya aberta, não zerar a faixa salva (blur/focus da sugestão).
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    const selectedText = String(el.value || '').slice(start, end);
    if (!selectedText.trim()) {
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    selRef.current = { start, end, text: selectedText };
    const pos = selectionBarPosition(el);
    if (!pos) {
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    setFmt({ ...pos, selectedText });
  }, [oryaCompose]);

  const openOryaCompose = useCallback(() => {
    const el = laudoRef.current;
    const pos = caretCoords(el) || { top: 120, left: 120 };
    setFmt(null);
    setOryaCompose(pos);
  }, []);

  const handleLaudoChange = (e) => {
    const el = e.target;
    const next = el.value;
    const caret = el.selectionStart ?? next.length;
    const before = next.slice(0, caret);
    const lineStart = before.lastIndexOf('\n') + 1;
    const lineBeforeCaret = before.slice(lineStart);
    if (lineBeforeCaret === '/' && !oryaCompose) {
      const cleaned = next.slice(0, lineStart) + next.slice(caret);
      setForm((p) => ({ ...p, laudoTecnico: cleaned }));
      requestAnimationFrame(() => {
        if (laudoRef.current) {
          laudoRef.current.selectionStart = lineStart;
          laudoRef.current.selectionEnd = lineStart;
        }
        openOryaCompose();
      });
      return;
    }
    setForm((p) => ({ ...p, laudoTecnico: next }));
    if (!fmtOpenRef.current) setFmt(null);
  };

  const resolveSelRange = useCallback((value) => {
    let { start, end, text: saved } = selRef.current || {};
    start = Number(start) || 0;
    end = Number(end) || 0;
    const v = String(value || '');
    if (end > start && end <= v.length && (!saved || v.slice(start, end) === saved)) {
      return { start, end };
    }
    if (saved) {
      const idx = v.indexOf(saved);
      if (idx >= 0) return { start: idx, end: idx + saved.length };
    }
    const caret = Math.min(Math.max(start, 0), v.length);
    return { start: caret, end: caret };
  }, []);

  const replaceSelection = useCallback(
    (replacement) => {
      const el = laudoRef.current;
      const value = String(el?.value ?? form.laudoTecnico);
      const { start, end } = resolveSelRange(value);
      const insert = String(replacement || '');
      const next = value.slice(0, start) + insert + value.slice(end);
      setForm((p) => ({ ...p, laudoTecnico: next }));
      setFmt(null);
      selRef.current = { start: 0, end: 0, text: '' };
      requestAnimationFrame(() => {
        if (!laudoRef.current) return;
        const pos = start + insert.length;
        laudoRef.current.focus();
        laudoRef.current.selectionStart = pos;
        laudoRef.current.selectionEnd = pos;
      });
    },
    [form.laudoTecnico, resolveSelRange]
  );

  const insertBelowSelection = useCallback(
    (suggestion) => {
      const base = String(selRef.current?.text || fmt?.selectedText || '').trimEnd();
      const add = String(suggestion || '');
      replaceSelection(base ? `${base}\n${add}` : add);
    },
    [fmt?.selectedText, replaceSelection]
  );

  const insertOryaText = useCallback(
    (content) => {
      const el = laudoRef.current;
      const value = String(el?.value ?? form.laudoTecnico);
      const caret = el?.selectionStart ?? value.length;
      const insert = String(content || '');
      const next = value.slice(0, caret) + insert + value.slice(caret);
      setForm((p) => ({ ...p, laudoTecnico: next }));
      setOryaCompose(null);
      requestAnimationFrame(() => {
        if (!laudoRef.current) return;
        const pos = caret + insert.length;
        laudoRef.current.focus();
        laudoRef.current.selectionStart = pos;
        laudoRef.current.selectionEnd = pos;
      });
    },
    [form.laudoTecnico]
  );

  const handleVoiceClick = useCallback(async () => {
    setVoiceError('');
    const rec = voiceRecRef.current;
    if (rec.recording) {
      rec.toggle();
      setVoiceAnchor(null);
      return;
    }
    const wrap = laudoBodyRef.current;
    const r = (wrap || laudoRef.current)?.getBoundingClientRect?.();
    if (r) {
      setVoiceAnchor({
        top: r.bottom + 6,
        left: Math.max(12, r.right - 268),
      });
    }
    await rec.toggle();
  }, []);

  const handleTranscribe = async () => {
    setVoiceError('');
    try {
      const rec = voiceRecRef.current;
      if (!rec.speechSupported) {
        await rec.cancel();
        setVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const text = String((await rec.finishTranscript()) || '').trim();
      if (!text) {
        setVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setForm((p) => {
        const cur = String(p.laudoTecnico || '').trim();
        return { ...p, laudoTecnico: cur ? `${cur} ${text}` : text };
      });
      setVoiceAnchor(null);
    } catch (e) {
      setVoiceError(e?.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  const openDatePicker = (which, btnRef) => {
    const rect = btnRef?.current?.getBoundingClientRect?.();
    if (!rect) return;
    setDatePicker({
      which,
      anchorRect: {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      },
      currentDate: which === 'ini' ? form.dataInicio : form.dataTermino,
    });
  };

  if (!open) return null;

  const handleSubmit = async () => {
    if (!canSave || saving) return;
    if (toMs(form.dataInicio, form.horaInicio) > toMs(form.dataTermino, form.horaTermino)) {
      setError('Início não pode ser depois do término.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/oficina/finalizar-os', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          numeroOS: osId,
          contadorPB: form.contadores.a4pb || '0',
          contadorCor: form.contadores.a4cor || '0',
          contadorDig: form.contadores.dig || '0',
          contadorA3PB: form.contadores.a3pb || '0',
          contadorA3Cor: form.contadores.a3cor || '0',
          codCondicao: form.codigoCondicao,
          laudoTecnico: form.laudoTecnico,
          dataIni: form.dataInicio,
          horaIni: form.horaInicio,
          dataFim: form.dataTermino ? `${form.dataTermino} 00:00:00.000` : null,
          horaFim: form.horaTermino || null,
          preAtendimento: true,
          codTec: String(linkedCodtech || agentCodtech || '').trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.success || data.resultado?.Sucesso !== 1) {
        throw new Error(data.resultado?.Mensagem || data.message || 'Não foi possível fechar a OS.');
      }
      await patchConversationOs(conversationId, osId, {
        status: 'closed',
        codtech: String(linkedCodtech || agentCodtech).trim(),
      });

      let trocaStatus = false;
      try {
        const cfgRes = await fetch(`${getApiBaseUrl()}/api/config/troca-status-conclusao`, {
          headers: authHeaders(),
        });
        const cfg = await cfgRes.json().catch(() => ({}));
        trocaStatus = Boolean(cfg?.success && String(cfg?.data?.codstatus || '00') === '01');
      } catch {
        trocaStatus = false;
      }

      if (trocaStatus) {
        setSelectedStatusCode('');
        setStatusFeedback(null);
        setShowStatusModal(true);
      } else {
        onClosed?.();
      }
    } catch (e) {
      setError(e.message || 'Falha ao fechar a OS.');
    } finally {
      setSaving(false);
    }
  };

  const calBounds = getCalendarBounds();

  return createPortal(
    <div className="bch-pre-close-overlay" role="dialog" aria-modal="true" aria-label={`Fechar OS ${osId}`}>
      {!showStatusModal ? (
        <>
          <button type="button" className="bch-pre-close-backdrop" aria-label="Fechar" onClick={onClose} />
          <div className="bch-pre-close-panel">
        <header className="bch-pre-close-head">
          <div className="bch-pre-close-head-main">
            <span className="bch-pre-close-os">OS {osId}</span>
            <span className="bch-pre-close-sub">Fechamento no pré-atendimento</span>
          </div>
          <button type="button" className="bch-pre-close-x" onClick={onClose} aria-label="Fechar">
            <IconX size={16} stroke={1.8} />
          </button>
        </header>

        <div className="bch-pre-close-body">
          {!(linkedCodtech || agentCodtech) ? (
            <p className="bch-pre-close-warn">
              Vincule o código do técnico no agente (Filas / Times → Pré-atendimento) para fechar a OS.
            </p>
          ) : null}

          <div className="bch-pre-close-datetime-grid">
            <div className="bch-pre-close-datetime-block">
              <span className="bch-pre-close-datetime-caption">Início</span>
              <div className="bch-pre-close-datetime-row">
                <button
                  ref={dateIniBtnRef}
                  type="button"
                  className="bch-pre-close-date-btn"
                  onClick={() => openDatePicker('ini', dateIniBtnRef)}
                >
                  <Calendar size={14} strokeWidth={2} aria-hidden />
                  <span>{formatPickerDate(form.dataInicio) || 'Data'}</span>
                </button>
                <input
                  type="time"
                  step="60"
                  className="bch-pre-close-time-input"
                  value={form.horaInicio}
                  onChange={(e) => setForm((p) => ({ ...p, horaInicio: e.target.value }))}
                  aria-label="Hora de início"
                />
              </div>
            </div>
            <div className="bch-pre-close-datetime-block">
              <span className="bch-pre-close-datetime-caption">Término</span>
              <div className="bch-pre-close-datetime-row">
                <button
                  ref={dateFimBtnRef}
                  type="button"
                  className="bch-pre-close-date-btn"
                  onClick={() => openDatePicker('fim', dateFimBtnRef)}
                >
                  <Calendar size={14} strokeWidth={2} aria-hidden />
                  <span>{formatPickerDate(form.dataTermino) || 'Data'}</span>
                </button>
                <input
                  type="time"
                  step="60"
                  className="bch-pre-close-time-input"
                  value={form.horaTermino}
                  onChange={(e) => setForm((p) => ({ ...p, horaTermino: e.target.value }))}
                  aria-label="Hora de término"
                />
              </div>
            </div>
          </div>

          <label className="bch-pre-close-field">
            Condição final
            <div className="bch-pre-close-cond" ref={condRef}>
              {form.condicaoFinal ? (
                <button
                  type="button"
                  className="bch-pre-close-cond-picked"
                  onClick={() => {
                    setForm((p) => ({ ...p, condicaoFinal: '', codigoCondicao: '' }));
                    setCondicaoQuery('');
                    setCondicaoOpen(true);
                  }}
                >
                  {form.condicaoFinal}
                  <span>✕</span>
                </button>
              ) : (
                <>
                  <input
                    type="text"
                    className="bch-pre-close-cond-input"
                    placeholder={
                      loadingCond ? 'Carregando…' : 'Digite 4+ caracteres para buscar...'
                    }
                    value={condicaoQuery}
                    disabled={loadingCond}
                    onChange={(e) => {
                      setCondicaoQuery(e.target.value);
                      setCondicaoOpen(true);
                    }}
                    onFocus={() => setCondicaoOpen(true)}
                    autoComplete="off"
                  />
                  {condicaoOpen && String(condicaoQuery).trim().length >= 4 ? (
                    <div className="bch-pre-close-cond-list" role="listbox">
                      {condicoesFiltered.length === 0 ? (
                        <div className="bch-pre-close-cond-empty">Nenhuma condição encontrada</div>
                      ) : (
                        condicoesFiltered.map((c, idx) => (
                          <button
                            key={`${c.TB01055_CODIGO}-${idx}`}
                            type="button"
                            className="bch-pre-close-cond-opt"
                            onClick={() => {
                              setForm((p) => ({
                                ...p,
                                condicaoFinal: condicaoLabel(c),
                                codigoCondicao: c.TB01055_CODIGO,
                              }));
                              setCondicaoQuery('');
                              setCondicaoOpen(false);
                            }}
                          >
                            {condicaoLabel(c)}
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </label>

          <div className="bch-pre-close-counters-wrap">
            {!showCounters ? (
              <button
                type="button"
                className="board-clickup-filter-chip bch-pre-close-counters-chip"
                onClick={() => setShowCounters(true)}
              >
                <Plus size={14} strokeWidth={2.2} aria-hidden />
                Contadores
              </button>
            ) : (
              <div className="bch-pre-close-counters">
                <div className="bch-pre-close-counters-head">
                  <span className="bch-pre-close-counters-title">Contadores</span>
                  <button
                    type="button"
                    className="bch-pre-close-counters-hide"
                    onClick={() => {
                      setShowCounters(false);
                      setShowA3(false);
                      setForm((p) => ({
                        ...p,
                        contadores: { a4pb: '', a4cor: '', dig: '', a3pb: '', a3cor: '' },
                      }));
                    }}
                  >
                    Ocultar
                  </button>
                </div>
                <div className="bch-pre-close-row bch-pre-close-row--5">
                  <label>
                    A4 PB
                    <input
                      inputMode="numeric"
                      value={form.contadores.a4pb}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contadores: { ...p.contadores, a4pb: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <label>
                    A4 Cor
                    <input
                      inputMode="numeric"
                      value={form.contadores.a4cor}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contadores: { ...p.contadores, a4cor: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <label>
                    DIG
                    <input
                      inputMode="numeric"
                      value={form.contadores.dig}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contadores: { ...p.contadores, dig: e.target.value },
                        }))
                      }
                    />
                  </label>
                  {showA3 ? (
                    <>
                      <label>
                        A3 PB
                        <input
                          inputMode="numeric"
                          value={form.contadores.a3pb}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              contadores: { ...p.contadores, a3pb: e.target.value },
                            }))
                          }
                        />
                      </label>
                      <label>
                        A3 Cor
                        <input
                          inputMode="numeric"
                          value={form.contadores.a3cor}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              contadores: { ...p.contadores, a3cor: e.target.value },
                            }))
                          }
                        />
                      </label>
                    </>
                  ) : (
                    <button type="button" className="bch-pre-close-a3" onClick={() => setShowA3(true)}>
                      + A3
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bch-pre-close-field bch-pre-close-laudo-field">
            <span className="bch-pre-close-field-label">Laudo técnico</span>
            <div className="bch-pre-close-laudo-body" ref={laudoBodyRef}>
              <textarea
                ref={laudoRef}
                rows={5}
                value={form.laudoTecnico}
                onChange={handleLaudoChange}
                onSelect={syncSelection}
                onMouseUp={syncSelection}
                onKeyUp={syncSelection}
                placeholder="Descreva o laudo… ( / para Orya )"
                aria-label="Laudo técnico"
              />
              <div className="bch-pre-close-laudo-tools">
                <button
                  type="button"
                  className={`bch-pre-close-voice-btn${voiceActive ? ' is-recording' : ''}`}
                  aria-label="Fala para texto"
                  onClick={handleVoiceClick}
                >
                  <span className="bch-pre-close-voice-tip" aria-hidden>
                    Grave em voz e tenha o texto transcrito aqui
                  </span>
                  <svg
                    className="bch-pre-close-voice-ico"
                    width="13"
                    height="13"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="6"
                      y="2"
                      width="4"
                      height="7"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M4 8a4 4 0 008 0M8 12v2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>Fala para texto</span>
                </button>
              </div>
            </div>
          </div>

          {error ? <p className="bch-pre-close-error">{error}</p> : null}
        </div>

        <footer className="bch-pre-close-foot">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={handleSubmit}
            disabled={!canSave || saving}
          >
            {saving ? <Loader2 size={14} className="bch-pre-close-spin" /> : null}
            Confirmar fechamento
          </button>
        </footer>
          </div>
        </>
      ) : null}

      {showStatusModal ? (
        <div className="bch-pre-close-status-backdrop" role="dialog" aria-modal="true" aria-labelledby="bch-pre-close-status-title">
          <div className="bch-pre-close-status-modal">
            <div className="bch-pre-close-status-header">
              <h2 id="bch-pre-close-status-title" className="bch-pre-close-status-title">
                Altere o status
              </h2>
            </div>
            <div className="bch-pre-close-status-body">
              {statusOptionsLoading ? (
                <div className="bch-pre-close-status-loading">Carregando opções...</div>
              ) : (
                <label className="bch-pre-close-status-label">
                  <span className="bch-pre-close-status-label-text">Novo status</span>
                  <div className="bch-pre-close-status-select" ref={statusSelectRef}>
                    <button
                      type="button"
                      className="bch-pre-close-status-trigger"
                      onClick={() => !statusSaving && setStatusSelectOpen((v) => !v)}
                      disabled={statusSaving}
                    >
                      <span>
                        {statusOptions.find((o) => String(o.codigo) === String(selectedStatusCode))?.nome ||
                          'Selecione...'}
                      </span>
                      <span aria-hidden>▼</span>
                    </button>
                    {statusSelectOpen ? (
                      <div className="bch-pre-close-status-dropdown" role="listbox">
                        {statusOptions.map((opt) => (
                          <button
                            key={String(opt.codigo)}
                            type="button"
                            className="bch-pre-close-status-option"
                            data-selected={String(opt.codigo) === String(selectedStatusCode)}
                            onClick={() => {
                              setSelectedStatusCode(String(opt.codigo));
                              setStatusSelectOpen(false);
                            }}
                          >
                            {opt.nome || opt.codigo}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </label>
              )}
            </div>
            <div className="bch-pre-close-status-footer">
              {statusFeedback === 'success' ? (
                <div className="bch-pre-close-status-feedback is-ok" role="status">
                  <CheckCircle size={16} strokeWidth={2} />
                  <span>Status alterado com sucesso</span>
                </div>
              ) : null}
              {statusFeedback === 'error' ? (
                <div className="bch-pre-close-status-feedback is-err" role="alert">
                  <X size={16} strokeWidth={2} />
                  <span>Não foi possível alterar. Tente novamente.</span>
                </div>
              ) : null}
              <button
                type="button"
                className="user-form-profile-save-btn user-form-footer-btn"
                onClick={handleConfirmStatus}
                disabled={statusSaving || !selectedStatusCode || statusFeedback === 'success'}
              >
                {statusSaving ? <Loader2 size={14} className="bch-pre-close-spin" /> : null}
                {statusFeedback === 'success' ? 'Concluído' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {datePicker && !showStatusModal ? (
        <PeriodModal
          key={`${datePicker.which}-${datePicker.currentDate || 'x'}`}
          anchorRect={datePicker.anchorRect}
          currentPeriod={{
            startDate: datePicker.currentDate || null,
            endDate: datePicker.currentDate || null,
          }}
          minDate={calBounds.minDate}
          maxDate={calBounds.maxDate}
          title={datePicker.which === 'ini' ? 'Data de início' : 'Data de término'}
          hideHeader
          singleMonth
          dayMode
          hideActionsBorder
          useUserFormActions
          modalClassName="bch-pre-close-period-modal ec-module-popover"
          zIndex={10120}
          onApply={(start) => {
            if (datePicker.which === 'ini') {
              setForm((p) => ({ ...p, dataInicio: start }));
            } else {
              setForm((p) => ({ ...p, dataTermino: start }));
            }
          }}
          onClear={() => {
            if (datePicker.which === 'ini') {
              const ini = partsFromCreatedAt(createdAt);
              setForm((p) => ({ ...p, dataInicio: ini.date }));
            } else {
              setForm((p) => ({ ...p, dataTermino: todayIso() }));
            }
          }}
          onClose={() => setDatePicker(null)}
        />
      ) : null}

      {oryaCompose ? (
        <CustomersNoticeOryaCompose
          position={oryaCompose}
          noticeTitle={`Laudo OS ${osId}`}
          keepOpenSelector=".bch-pre-close-panel, .bch-pre-close-laudo-body textarea"
          onInsert={insertOryaText}
          onClose={() => setOryaCompose(null)}
        />
      ) : null}

      {fmt && !oryaCompose ? (
        <CustomersNoticeFmtBar
          aiOnly
          position={{ top: fmt.top, left: fmt.left }}
          selectedText={fmt.selectedText}
          keepOpenSelector=".bch-pre-close-panel, .bch-pre-close-laudo-body textarea"
          onReplaceSelection={replaceSelection}
          onInsertBelow={insertBelowSelection}
          onClose={() => setFmt(null)}
        />
      ) : null}

      {voiceActive || voiceError ? (
        <div
          className="bch-pre-close-voice-pop"
          style={
            voiceAnchor
              ? { top: `${voiceAnchor.top}px`, left: `${voiceAnchor.left}px` }
              : undefined
          }
          role="status"
          aria-live="polite"
        >
          {voiceActive ? (
            <>
              <span
                className={`bch-pre-close-voice-pulse${voiceRecorder.busy ? ' is-busy' : ''}`}
                aria-hidden
              />
              <span className="bch-pre-close-voice-timer">
                {`${Math.floor((voiceRecorder.elapsedSec || 0) / 60)}:${String(
                  (voiceRecorder.elapsedSec || 0) % 60
                ).padStart(2, '0')}`}
              </span>
              <span className="bch-pre-close-voice-label">
                {voiceRecorder.busy ? 'Processando…' : 'Gravando…'}
              </span>
              <button
                type="button"
                className="bch-pre-close-voice-transcribe"
                disabled={voiceRecorder.busy}
                onClick={handleTranscribe}
              >
                Transcrever
              </button>
              <button
                type="button"
                className="bch-pre-close-voice-cancel"
                disabled={voiceRecorder.busy}
                aria-label="Cancelar gravação"
                onClick={stopVoice}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </>
          ) : null}
          {voiceError ? <div className="bch-pre-close-voice-error">{voiceError}</div> : null}
        </div>
      ) : null}
    </div>,
    document.body
  );
}
